'use client'

import { useMemo, useState } from 'react'

import { XIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'

import { useImageActions } from '@/components/business/prompt/hooks/use-image-actions'
import { ImageViewer } from '@/components/common/image/image-viewer'
import { ResolvedImage } from '@/components/common/image/resolved-image'
import { useAppContext } from '@/components/providers/app-provider'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger
} from '@/components/ui/drawer'
import { useUploader } from '@/lib/api/hooks/common'
import {
  useAddResource,
  useDeleteResource,
  useResourceListInfinite,
  useUpdateResource
} from '@/lib/api/hooks/resource'
import type { ImageResource } from '@/lib/api/models/image'
import type { Resource } from '@/lib/api/models/resource'
import { ResourceTagId, ResourceVisible } from '@/lib/api/models/resource'
import { getNextRm32RefRole, RM32_REF_MAX_IMAGES } from '@/lib/config/rm32-ref'
import {
  getCharacterLocalBlobUrl,
  persistUploadedImageCache,
  rememberCharacterLocalPreview,
  resolveRemoteDisplayUrl,
  toCharacterResourceId,
  type ImageDisplaySource
} from '@/lib/image-cache'
import { cn } from '@/lib/utils'
import { batchDownloadMedia } from '@/lib/utils/download'
import { getFileExtension } from '@/lib/utils/file'
import { selectCurrentModel, useImagesStore, useModelStore, type ImageRole } from '@/stores'

import {
  UploadAssetContent,
  UploadAssetDialog,
  type AssetUploadCategory,
  type UploadAssetData
} from './upload-asset-dialog'

export type AssetCategory = 'all' | 'characters' | 'props' | 'scene' | 'imageGallery'

export interface Asset {
  id: string
  /** 后端资源 ID（仅 API 来源的资源有；mock 数据为空） */
  resourceId?: number
  /** 网格展示与选择时优先使用的 URL（通常为 show_image_url） */
  url: string
  category: Exclude<AssetCategory, 'all'>
  selected?: boolean
  /** 用于回填编辑表单 */
  name?: string
  description?: string
  /** 后端附属字段，更新/复用时一起带回 */
  showImageUrl?: string
  imageUrl?: string
  imageUrlV1?: string
  thumbnailUrl?: string
  thumbnailUrlV1?: string
  width?: number
  height?: number
}

/** UI 分类 ↔ 后端 tag_id 映射 */
const CATEGORY_TO_TAG_ID: Record<AssetUploadCategory, ResourceTagId> = {
  characters: ResourceTagId.CHARACTER,
  props: ResourceTagId.PROPS,
  scene: ResourceTagId.SCENE
}

const TAG_ID_TO_CATEGORY: Record<number, Exclude<AssetCategory, 'all'>> = {
  [ResourceTagId.CHARACTER]: 'characters',
  [ResourceTagId.PROPS]: 'props',
  [ResourceTagId.SCENE]: 'scene',
  [ResourceTagId.IMAGE_GALLERY]: 'imageGallery'
}

function isSignedObjectUrl(url: string): boolean {
  return /[?&]X-Amz-/i.test(url) || /[?&]x-amz-/i.test(url)
}

function isObjectStoreHost(hostname: string): boolean {
  return hostname.endsWith('wasabisys.com') || hostname.endsWith('amazonaws.com')
}

/** 浏览器 / Next Image 代理能直接拉到的原图：签名直链，或公网 CDN。未签名的 Wasabi/S3 会 403。 */
function isLikelyPublicImageUrl(url?: string): boolean {
  if (!url) return false
  try {
    const parsed = new URL(url)
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false
    if (/\/thumbnail\//i.test(parsed.pathname)) return false
    if (isObjectStoreHost(parsed.hostname)) return isSignedObjectUrl(url)
    const host = parsed.hostname
    return host.endsWith('cloudfront.net') || host === 'digen.ai' || host.endsWith('.digen.ai')
  } catch {
    return false
  }
}

/**
 * 编辑弹窗 / 刚上传后的展示要用原图。
 * chip 用的 80px dataUrl、以及 `/thumbnail/` 缩略图放大到 478px 都会发糊。
 */
function pickAssetOriginalUrl(
  asset: Pick<
    Asset,
    | 'url'
    | 'resourceId'
    | 'id'
    | 'showImageUrl'
    | 'imageUrlV1'
    | 'imageUrl'
    | 'thumbnailUrlV1'
    | 'thumbnailUrl'
  > | null
    | undefined
): string {
  if (!asset) return ''
  if (asset.url.startsWith('blob:')) return asset.url

  const localOriginal = getCharacterLocalBlobUrl(asset.resourceId ?? asset.id)
  if (localOriginal) return localOriginal

  const publicOriginals = [asset.showImageUrl, asset.imageUrlV1, asset.imageUrl].filter(
    (url): url is string => isLikelyPublicImageUrl(url)
  )
  if (publicOriginals[0]) return publicOriginals[0]

  const signedFallback = [
    asset.showImageUrl,
    asset.thumbnailUrlV1,
    asset.thumbnailUrl,
    asset.url
  ].find((url): url is string => !!url && isSignedObjectUrl(url))
  if (signedFallback) return signedFallback

  return asset.thumbnailUrlV1 || asset.thumbnailUrl || asset.url
}

/** Resource(API) → Asset(UI) */
const resourceToAsset = (resource: Resource): Asset => {
  const showImageUrl = resource.show_image_url?.trim()
  const displayUrl = pickAssetOriginalUrl({
    id: String(resource.id),
    resourceId: resource.id,
    url: '',
    showImageUrl: showImageUrl || undefined,
    imageUrl: resource.image_url,
    imageUrlV1: resource.image_url_v1,
    thumbnailUrl: resource.thumbnail_url,
    thumbnailUrlV1: resource.thumbnail_url_v1
  })

  return {
    id: String(resource.id),
    resourceId: resource.id,
    url: displayUrl,
    category: TAG_ID_TO_CATEGORY[resource.tag_id] ?? 'characters',
    name: resource.name,
    description: resource.description,
    showImageUrl: showImageUrl || undefined,
    imageUrl: resource.image_url,
    imageUrlV1: resource.image_url_v1,
    thumbnailUrl: resource.thumbnail_url,
    thumbnailUrlV1: resource.thumbnail_url_v1,
    width: resource.width,
    height: resource.height
  }
}

const MOCK_ASSETS: Asset[] = [
  { id: '1', url: 'https://picsum.photos/seed/char1/400/400', category: 'characters' },
  { id: '2', url: 'https://picsum.photos/seed/char2/400/400', category: 'characters' },
  { id: '3', url: 'https://picsum.photos/seed/char3/400/400', category: 'characters' },
  { id: '4', url: 'https://picsum.photos/seed/char4/400/400', category: 'characters' },
  { id: '5', url: 'https://picsum.photos/seed/char5/400/400', category: 'characters' },
  { id: '6', url: 'https://picsum.photos/seed/char6/400/400', category: 'characters' },
  { id: '7', url: 'https://picsum.photos/seed/prop1/400/400', category: 'props' },
  { id: '8', url: 'https://picsum.photos/seed/prop2/400/400', category: 'props' },
  { id: '9', url: 'https://picsum.photos/seed/prop3/400/400', category: 'props' },
  { id: '10', url: 'https://picsum.photos/seed/prop4/400/400', category: 'props' },
  { id: '11', url: 'https://picsum.photos/seed/scene1/400/400', category: 'scene' },
  { id: '12', url: 'https://picsum.photos/seed/scene2/400/400', category: 'scene' },
  { id: '13', url: 'https://picsum.photos/seed/scene3/400/400', category: 'scene' },
  { id: '14', url: 'https://picsum.photos/seed/scene4/400/400', category: 'scene' },
  { id: '15', url: 'https://picsum.photos/seed/scene5/400/400', category: 'scene' },
  { id: '16', url: 'https://picsum.photos/seed/img1/400/400', category: 'imageGallery' },
  { id: '17', url: 'https://picsum.photos/seed/img2/400/400', category: 'imageGallery' },
  { id: '18', url: 'https://picsum.photos/seed/img3/400/400', category: 'imageGallery' },
  { id: '19', url: 'https://picsum.photos/seed/img4/400/400', category: 'imageGallery' },
  { id: '20', url: 'https://picsum.photos/seed/img5/400/400', category: 'imageGallery' },
  { id: '21', url: 'https://picsum.photos/seed/img6/400/400', category: 'imageGallery' },
  { id: '22', url: 'https://picsum.photos/seed/img7/400/400', category: 'imageGallery' },
  { id: '23', url: 'https://picsum.photos/seed/img8/400/400', category: 'imageGallery' },
  { id: '24', url: 'https://picsum.photos/seed/img9/400/400', category: 'imageGallery' }
]

export type AssetLibraryMode = 'asset' | 'character'

/**
 * 选择行为：
 * - `'add-image'`（默认）：选中后调用 `selectFromAlbum` 将图片加入到图片槽位
 * - `'pick'`：仅向上回调，不产生其它副作用（用于纯选择器场景，例如 @ 提及角色）
 */
export type AssetLibrarySelectBehavior = 'add-image' | 'pick'

export interface AssetLibraryDialogProps {
  /** 触发器；如使用受控 `open` 也可省略 */
  children?: React.ReactNode
  onSelect?: (asset: Asset) => void
  /**
   * 弹窗模式：
   * - `'asset'`（默认）：完整资产库，显示全部 tabs
   * - `'character'`：角色库，标题切换为"角色库"、初始 tab 锁定为 characters、隐藏 tabs 切换栏
   */
  mode?: AssetLibraryMode
  /** 选择行为，默认 `'add-image'` */
  selectBehavior?: AssetLibrarySelectBehavior
  /** 受控 open 状态；省略时使用内部状态 */
  open?: boolean
  /** 受控 open 状态变化回调 */
  onOpenChange?: (open: boolean) => void
  /** 写入图片槽位时使用的 role，默认 `image` */
  imageRole?: ImageRole
  /** 覆盖模型 maxImages，用于 RM32 等多槽位场景 */
  maxImagesOverride?: number
  /** RM3.2 Ref：按 场景→角色→参考图 顺序自动分配槽位 */
  autoRm32RefSlot?: boolean
  /** 弹窗尺寸：`compact` 用于导演控制台等嵌套场景（920×560） */
  dialogSize?: 'default' | 'compact'
  /** 覆盖弹窗内容层级/样式（用于嵌套在更高 z-index 容器内，如 storyboard 弹窗） */
  contentClassName?: string
  /** 覆盖遮罩层级/样式 */
  overlayClassName?: string
}

const ASSET_LIBRARY_DIALOG_CONTENT_CLASS = {
  default:
    'z-90 flex h-[890px] w-[1288px] max-w-[90vw] flex-col gap-0 overflow-hidden border-0 bg-transparent p-0',
  compact:
    'z-90 flex h-[560px] w-full max-w-[920px] flex-col gap-0 overflow-hidden border-0 bg-transparent p-0'
} as const

export const AssetLibraryDialog = ({
  children,
  onSelect,
  mode = 'asset',
  selectBehavior = 'add-image',
  open: openProp,
  onOpenChange,
  imageRole = 'image',
  maxImagesOverride,
  autoRm32RefSlot = false,
  dialogSize = 'default',
  contentClassName,
  overlayClassName
}: AssetLibraryDialogProps) => {
  const { isMobile } = useAppContext()
  const [internalOpen, setInternalOpen] = useState(false)
  const { selectFromAlbum } = useImageActions()
  const t = useTranslations('common')

  const isControlled = openProp !== undefined
  const open = isControlled ? !!openProp : internalOpen

  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next)
    onOpenChange?.(next)
  }

  /**
   * 把 Asset 转成上层 ImageResource。
   *
   * 注意：后端 image_url / image_url_v1 / thumbnail_url 等字段实际指向私网地址，
   * Next/Image 服务端代理会因 DNS 解析到私网 IP 而拒绝（resolved to private ip），
   * 导致上方预览图回落到 ImageFallback。
   *
   * 这里优先使用 asset.url（= show_image_url，带签名公网 URL），保证显示正常。
   * V1 字段同样兜底到 asset.url，宁可签名短期可能过期，也比私网无法访问更友好。
   */
  const toImageResource = (asset: Asset): ImageResource => {
    const localPreview = asset.url?.startsWith('blob:') ? asset.url : undefined
    const displayUrl =
      localPreview ||
      asset.showImageUrl ||
      asset.imageUrlV1 ||
      asset.thumbnailUrlV1 ||
      asset.url ||
      asset.thumbnailUrl ||
      asset.imageUrl ||
      ''
    return {
      thumbnail: displayUrl,
      thumbnailV1: asset.thumbnailUrlV1 || displayUrl,
      image: asset.imageUrl || displayUrl,
      imgV1: asset.imageUrlV1 || displayUrl
    }
  }

  const resolveSelectRole = (): ImageRole | null => {
    if (autoRm32RefSlot) {
      return getNextRm32RefRole(useImagesStore.getState().imageSlots)
    }
    return imageRole
  }

  const resolveCharacterSlotExtra = (asset: Asset) => {
    const resourceId = toCharacterResourceId(asset.resourceId ?? asset.id)
    const localPreview =
      getCharacterLocalBlobUrl(resourceId) ||
      (asset.url?.startsWith('blob:') ? asset.url : undefined)
    return {
      id: resourceId ? `character-${resourceId}` : undefined,
      localPreviewUrl: localPreview
    }
  }

  const handleSelect = (asset: Asset) => {
    if (selectBehavior === 'pick') {
      setOpen(false)
      onSelect?.(asset)
      return
    }

    void (async () => {
      const role = resolveSelectRole()
      if (!role) {
        toast.error(t('selectionLimitReached', { count: RM32_REF_MAX_IMAGES }))
        return
      }
      // asset.name 用于在 prompt 中作为 @ 提及的展示名，便于联动 character-mention 的同步逻辑
      await selectFromAlbum(toImageResource(asset), role, asset.name, resolveCharacterSlotExtra(asset))
      setOpen(false)
      onSelect?.(asset)
    })()
  }

  /** 多选态批量使用：依次落到图片槽位（或 pick 模式下回调每一个） */
  const handleUseMultiple = (assets: Asset[]) => {
    if (assets.length === 0) return

    if (selectBehavior === 'pick') {
      setOpen(false)
      assets.forEach(a => onSelect?.(a))
      return
    }

    void (async () => {
      for (const asset of assets) {
        const role = resolveSelectRole()
        if (!role) {
          toast.error(t('selectionLimitReached', { count: RM32_REF_MAX_IMAGES }))
          break
        }
        await selectFromAlbum(
          toImageResource(asset),
          role,
          asset.name,
          resolveCharacterSlotExtra(asset)
        )
        onSelect?.(asset)
      }
      setOpen(false)
    })()
  }

  const a11yTitle = mode === 'character' ? 'Character Library' : 'Asset Library'

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        {children && <DrawerTrigger asChild>{children}</DrawerTrigger>}
        <DrawerContent
          className={cn('z-90 flex h-[85vh] border-0 bg-transparent p-0', contentClassName)}
        >
          <DrawerTitle className="sr-only">{a11yTitle}</DrawerTitle>
          <DrawerDescription className="sr-only">{a11yTitle}</DrawerDescription>
          <AssetLibraryContent
            compact={dialogSize === 'compact'}
            maxImagesOverride={maxImagesOverride}
            mode={mode}
            selectBehavior={selectBehavior}
            onClose={() => setOpen(false)}
            onSelect={handleSelect}
            onUseMultiple={handleUseMultiple}
          />
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent
        className={cn(ASSET_LIBRARY_DIALOG_CONTENT_CLASS[dialogSize], contentClassName)}
        overlayClassName={cn('bg-black/50', overlayClassName)}
        showCloseButton={false}
      >
        <DialogTitle className="sr-only">{a11yTitle}</DialogTitle>
        <DialogDescription className="sr-only">{a11yTitle}</DialogDescription>
        <AssetLibraryContent
          compact={dialogSize === 'compact'}
          maxImagesOverride={maxImagesOverride}
          mode={mode}
          selectBehavior={selectBehavior}
          onClose={() => setOpen(false)}
          onSelect={handleSelect}
          onUseMultiple={handleUseMultiple}
        />
      </DialogContent>
    </Dialog>
  )
}

interface AssetLibraryContentProps {
  onSelect?: (asset: Asset) => void
  /** 多选确认时调用，传入所有选中的 asset */
  onUseMultiple?: (assets: Asset[]) => void
  onClose?: () => void
  mode?: AssetLibraryMode
  /**
   * 透传自父组件，用于决定是否启用模型 `maxImages` 封顶：
   * - `'add-image'`：会落到 image slot，受 `maxImages` 限制
   * - `'pick'`     ：纯回调（如 @ 提及），不受 `maxImages` 限制
   */
  selectBehavior?: AssetLibrarySelectBehavior
  maxImagesOverride?: number
  compact?: boolean
}

const AssetLibraryContent = ({
  onSelect,
  onUseMultiple,
  onClose,
  mode = 'asset',
  selectBehavior = 'add-image',
  maxImagesOverride,
  compact = false
}: AssetLibraryContentProps) => {
  const t = useTranslations('common')
  const { isMobile } = useAppContext()
  const isCharacterMode = mode === 'character'
  const actionButtonClass = cn(
    'flex shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md transition-colors',
    isMobile ? 'px-2.5 py-1.5' : 'px-4 py-2'
  )
  /**
   * 角色库模式下，tabs 区只显示"全部"一项；语义上等于"全部角色"，
   * 因此 activeTab 始终为 'all'，由 filteredAssets 把数据钉死在 characters 类目上
   */
  const [activeTab, setActiveTab] = useState<AssetCategory>('all')
  /**
   * 头部右侧动作模式：
   * - `default`：默认按钮组 [删除?][选择多个角色][上传角色]
   * - `select` ：使用 N 个的选择态 [下载][取消][使用 N 个]
   * - `delete` ：删除选择态 [删除][取消]，二者互斥
   */
  const [actionMode, setActionMode] = useState<'default' | 'select' | 'delete'>('default')
  const isMultiSelect = actionMode === 'select'
  const isDeleteMode = actionMode === 'delete'
  /** 任一选择态都允许卡片勾选 */
  const isPickingActive = actionMode !== 'default'
  const [mockAssets, setMockAssets] = useState<Asset[]>(MOCK_ASSETS)
  const [selectedAssets, setSelectedAssets] = useState<Set<string>>(new Set())
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null)
  /** 编辑时用户替换的新文件（保存时拿去上传） */
  const [editingFile, setEditingFile] = useState<File | null>(null)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [viewerSource, setViewerSource] = useState<ImageDisplaySource | null>(null)
  /**
   * 删除二次确认：
   * - 列表头部"删除"按钮：拿当前 selectedAssets 作为待删集合
   * - 单卡片悬浮"删除"按钮：传入单个资源 id 作为待删集合
   */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([])
  const [isDeleting, setIsDeleting] = useState(false)

  const closeEditing = () => {
    setEditingAsset(null)
    setEditingFile(null)
  }

  /**
   * 角色库走真实 API：tag_id 钉死为 CHARACTER。
   * 资产库（dev 入口）暂保留 mock，待后端拉通其它 tag 后再切换。
   */
  const { list: characterResources, query: characterQuery } = useResourceListInfinite(
    { tag_id: ResourceTagId.CHARACTER },
    100
  )

  const uploader = useUploader()
  const addResourceMutation = useAddResource()
  const updateResourceMutation = useUpdateResource()
  const deleteResourceMutation = useDeleteResource()
  const [isBatchDownloading, setIsBatchDownloading] = useState(false)

  /**
   * 当前选中模型支持的最大图片数（standard image slot 口径）。
   * - 与 `selectFromAlbum` 保持一致：直接读 store 原始值，缺省为 1
   * - 仅在「使用 N 个」选择态 + `selectBehavior === 'add-image'` 时封顶
   * - `pick` 模式（@ 提及角色等）不受限制
   * - 删除态不受限制
   *
   * 注意：「多选」入口在所有模型下均常驻显示；
   * 超出 `maxImages` 时由 `handleAssetClick` 阻断并 toast 提醒。
   */
  const currentModel = useModelStore(selectCurrentModel)
  const maxImages = maxImagesOverride ?? currentModel?.maxImages ?? 1
  /** 是否真正受 `maxImages` 约束（仅 add-image 模式） */
  const respectMaxImages = selectBehavior === 'add-image'

  /** 真实 API 数据映射成 UI Asset，用于角色库模式 */
  const apiAssets = useMemo<Asset[]>(
    () => characterResources.map(resourceToAsset),
    [characterResources]
  )

  const isApiLoading = isCharacterMode && characterQuery.isPending
  const isRefreshing = isCharacterMode && characterQuery.isFetching && !characterQuery.isPending

  const tabs: { value: AssetCategory; labelKey: string; icon: React.ReactNode }[] = [
    { value: 'all', labelKey: 'all', icon: <AllIcon /> },
    { value: 'characters', labelKey: 'characters', icon: <CharactersIcon /> },
    { value: 'props', labelKey: 'props', icon: <PropsIcon /> },
    { value: 'scene', labelKey: 'scene', icon: <SceneIcon /> },
    { value: 'imageGallery', labelKey: 'imageGallery', icon: <ImageGalleryIcon /> }
  ]

  const filteredAssets = useMemo(() => {
    if (isCharacterMode) {
      // 角色库永远只显示 characters 类目（数据已按 tag_id=CHARACTER 拉取）
      return apiAssets
    }
    if (activeTab === 'all') {
      return mockAssets
    }
    return mockAssets.filter(asset => asset.category === activeTab)
  }, [apiAssets, mockAssets, activeTab, isCharacterMode])

  const handleRefresh = () => {
    if (isCharacterMode) {
      void characterQuery.refetch()
      return
    }
    // 非角色库的 mock 数据，沿用原行为
    setMockAssets(MOCK_ASSETS)
  }

  const handleAssetClick = (asset: Asset) => {
    if (!isPickingActive) {
      onSelect?.(asset)
      return
    }

    setSelectedAssets(prev => {
      // 已选中 → 取消选中（不论何种模式都允许）
      if (prev.has(asset.id)) {
        const next = new Set(prev)
        next.delete(asset.id)
        return next
      }

      /**
       * 勾选不再做模型 `maxImages` 阻断。
       * - 体验上允许用户先勾再调整，超额会在「使用 N 个」按钮的点击校验中提示
       * - 删除态本身不受 `maxImages` 限制
       */
      const next = new Set(prev)
      next.add(asset.id)
      return next
    })
  }

  /** 单卡片"删除"按钮：用单条资源发起二次确认（不依赖多选状态） */
  const handleRequestDeleteOne = (assetId: string) => {
    setPendingDeleteIds([assetId])
    setDeleteConfirmOpen(true)
  }

  /** 进入选择态（"使用 N 个"流程） */
  const enterSelectMode = () => {
    setActionMode('select')
    setSelectedAssets(new Set())
  }

  /** 进入删除态（"删除多张图片"流程） */
  const enterDeleteMode = () => {
    setActionMode('delete')
    setSelectedAssets(new Set())
  }

  /** 退出任意选择/删除态，回到默认按钮组 */
  const exitActionMode = () => {
    setActionMode('default')
    setSelectedAssets(new Set())
  }

  /** 删除模式下提交：把当前 selectedAssets 作为待删集合发起二次确认 */
  const handleRequestDeleteSelected = () => {
    if (selectedAssets.size === 0) return
    setPendingDeleteIds(Array.from(selectedAssets))
    setDeleteConfirmOpen(true)
  }

  /** 当前在 grid 中被勾选的资源（按显示顺序） */
  const getSelectedAssetList = () => filteredAssets.filter(asset => selectedAssets.has(asset.id))

  /** 多选态：批量下载（沿用 batchDownloadMedia 串行下载逻辑） */
  const handleBatchDownload = async () => {
    const selected = getSelectedAssetList()
    if (selected.length === 0 || isBatchDownloading) return
    setIsBatchDownloading(true)
    try {
      const urls = selected.map(a => a.url)
      const results = await batchDownloadMedia(urls, { mediaType: 'image' })
      const failed = results.filter(r => !r.success).length
      if (failed > 0) {
        toast.error(t('uploadFailed'))
      }
    } finally {
      setIsBatchDownloading(false)
    }
  }

  /**
   * 多选态：使用 N 个资产
   * - 仅在 `add-image` 流程下做 `maxImages` 校验
   * - 超额：toast 提示并阻断，让用户先取消多余勾选（避免偷偷截断）
   */
  const handleUseSelected = () => {
    const selected = getSelectedAssetList()
    if (selected.length === 0) return

    if (respectMaxImages && selected.length > maxImages) {
      toast.warning(t('selectionLimitReached', { count: maxImages }))
      return
    }

    onUseMultiple?.(selected)
  }

  /**
   * 二次确认后真正执行删除。
   * - 角色库模式：逐个调用后端 /v1/resource/del
   * - 资产库 mock 模式：直接从本地状态移除
   */
  const handleConfirmDelete = async () => {
    if (pendingDeleteIds.length === 0) return
    setIsDeleting(true)
    try {
      if (isCharacterMode) {
        const idSet = new Set(pendingDeleteIds)
        const targets = apiAssets.filter(a => idSet.has(a.id) && a.resourceId)
        const results = await Promise.allSettled(
          targets.map(a => deleteResourceMutation.mutateAsync({ id: a.resourceId! }))
        )
        const failed = results.filter(r => r.status === 'rejected').length
        if (failed > 0) {
          toast.error(t('deleteFailed'))
        } else if (results.length > 0) {
          toast.success(t('deleteSuccess'))
        }
      } else {
        const idSet = new Set(pendingDeleteIds)
        setMockAssets(prev => prev.filter(asset => !idSet.has(asset.id)))
      }

      // 把已经删除掉的 id 从多选集合里清理出去
      setSelectedAssets(prev => {
        if (prev.size === 0) return prev
        const next = new Set(prev)
        for (const id of pendingDeleteIds) next.delete(id)
        return next
      })

      // 删除模式下，提交成功后自动回到默认态
      if (isDeleteMode) {
        setActionMode('default')
      }
    } finally {
      setIsDeleting(false)
      setDeleteConfirmOpen(false)
      setPendingDeleteIds([])
    }
  }

  /**
   * 新建资源：直接把文件以 multipart 提交到 /v1/resource/addV1。
   *
   * 仅角色库模式接通后端；资产库模式继续使用本地 mock 占位。
   */
  const handleUploadSave = async (data: UploadAssetData) => {
    if (!isCharacterMode) {
      const newAsset: Asset = {
        id: `upload-${Date.now()}`,
        url: data.url,
        category: data.category
      }
      setMockAssets(prev => [newAsset, ...prev])
      return
    }

    if (!data.file) {
      toast.error(t('uploadFailed'))
      return
    }

    try {
      const created = await addResourceMutation.mutateAsync({
        file: data.file,
        name: data.name || data.file.name,
        description: data.description,
        tag_id: CATEGORY_TO_TAG_ID[data.category] ?? ResourceTagId.CHARACTER,
        is_visible: ResourceVisible.VISIBLE,
        format: getFileExtension(data.file.name) || undefined
      })
      const createdId =
        toCharacterResourceId(
          created && typeof created === 'object'
            ? ((created as { id?: unknown }).id ?? created)
            : created
        )
      if (createdId) {
        await rememberCharacterLocalPreview(createdId, data.file)
      }
      await characterQuery.refetch()

      toast.success(t('saveSuccess'))
    } catch (error) {
      console.error('[asset-library] upload/add resource failed:', error)
      toast.error(t('saveFailed'))
      throw error
    }
  }

  /**
   * 编辑资源：
   * - 角色库模式调 /v1/resource/update，文件有变动则先上传换 URL
   * - 非角色库模式仍走本地 mock 替换
   */
  const handleEditSave = async (data: UploadAssetData) => {
    if (!editingAsset) return

    if (!isCharacterMode || !editingAsset.resourceId) {
      setMockAssets(prev =>
        prev.map(asset =>
          asset.id === editingAsset.id
            ? { ...asset, url: data.url, category: data.category }
            : asset
        )
      )
      closeEditing()
      return
    }

    try {
      let imageUrl: string | undefined
      let imageUrlV1: string | undefined
      let thumbnailUrl: string | undefined
      let thumbnailUrlV1: string | undefined
      let width: number | undefined
      let height: number | undefined

      // 用户替换了图片（editingFile 优先级高于 data.file），先上传拿到新 URL
      const fileToUpload = editingFile ?? data.file
      if (fileToUpload) {
        const uploaded = await uploader.mutateAsync({
          file: fileToUpload,
          format: getFileExtension(fileToUpload.name) ?? '',
          location: 'main'
        })
        await persistUploadedImageCache(fileToUpload, uploaded)
        imageUrl = uploaded.imageUrl
        imageUrlV1 = uploaded.imageUrlV1
        thumbnailUrl = uploaded.thumbnailUrl
        thumbnailUrlV1 = uploaded.thumbnailUrlV1
        width = uploaded.width
        height = uploaded.height
      }

      await updateResourceMutation.mutateAsync({
        id: editingAsset.resourceId,
        name: data.name,
        description: data.description,
        tag_id: CATEGORY_TO_TAG_ID[data.category] ?? ResourceTagId.CHARACTER,
        ...(imageUrl ? { image_url: imageUrl } : {}),
        ...(imageUrlV1 ? { image_url_v1: imageUrlV1 } : {}),
        ...(thumbnailUrl ? { thumbnail_url: thumbnailUrl } : {}),
        ...(thumbnailUrlV1 ? { thumbnail_url_v1: thumbnailUrlV1 } : {}),
        ...(width ? { width } : {}),
        ...(height ? { height } : {})
      })

      toast.success(t('saveSuccess'))
      closeEditing()
    } catch (error) {
      console.error('[asset-library] update resource failed:', error)
      toast.error(t('saveFailed'))
      throw error
    }
  }

  const editingInitialValues = useMemo(() => {
    if (!editingAsset) return undefined
    const category: AssetUploadCategory | null =
      editingAsset.category === 'characters' ||
      editingAsset.category === 'props' ||
      editingAsset.category === 'scene'
        ? editingAsset.category
        : null
    return {
      previewUrl: editingAsset.url,
      category,
      name: editingAsset.name,
      description: editingAsset.description
    }
  }, [editingAsset])

  const isSavingUpload = addResourceMutation.isPending
  const isSavingEdit = uploader.isPending || updateResourceMutation.isPending
  const isLoading = isApiLoading
  const isEmpty = !isLoading && filteredAssets.length === 0

  return (
    <div
      style={{ boxShadow: '0px 8px 20px 0px rgba(0, 0, 0, 0.4)' }}
      className={cn(
        'relative flex size-full flex-col overflow-hidden border border-[rgba(255,255,255,0.08)] bg-light-800',
        compact ? 'rounded-lg' : 'rounded-3xl'
      )}
    >
      {/* Header */}
      <div
        className={cn(
          'relative flex shrink-0 items-center',
          isMobile ? 'h-12 px-4' : cn('px-6', compact ? 'h-12' : 'h-[52px]')
        )}
      >
        <h2
          className={cn(
            "font-['Inter'] font-bold text-[#dadada]",
            isMobile ? 'text-base leading-6' : 'text-[18px] leading-[28px]'
          )}
        >
          {isCharacterMode ? t('characterLibrary') : t('assetLibrary')}
        </h2>

        {!isCharacterMode && (
          <>
            {/* 分隔符 */}
            <div
              className={cn(
                'absolute flex h-5 w-[17px] items-center overflow-hidden px-2',
                isMobile ? 'top-1/2 right-11 -translate-y-1/2' : 'top-[27px] right-[55px]'
              )}
            >
              <div className="h-5 w-px bg-[rgba(255,255,255,0.08)]" />
            </div>

            {/* 刷新按钮 */}
            <button
              className={cn(
                'absolute flex size-7 items-center justify-center rounded-md hover:bg-light/10',
                isMobile ? 'top-1/2 right-[52px] -translate-y-1/2' : 'top-6 right-[77px]'
              )}
              onClick={handleRefresh}
            >
              <RefreshIcon className={cn((isLoading || isRefreshing) && 'animate-spin')} />
            </button>
          </>
        )}

        {/* 关闭按钮 */}
        <button
          className={cn(
            'absolute flex size-7 items-center justify-center rounded-md hover:bg-light/10',
            isMobile ? 'top-1/2 right-4 -translate-y-1/2' : 'top-6 right-6'
          )}
          onClick={onClose}
        >
          <XIcon className="size-5 text-[#dadada]" />
        </button>
      </div>

      {/* Tabs + 操作区：同一行，移动端用 flex 避免 absolute 重叠，按钮更紧凑 */}
      <div
        className={cn(
          'flex shrink-0 items-center gap-2 overflow-x-auto',
          isMobile ? 'px-4' : 'px-6'
        )}
      >
        <div className="flex shrink-0 items-center">
          {(isCharacterMode ? tabs.filter(tab => tab.value === 'all') : tabs).map(tab => {
            const isActive = activeTab === tab.value
            return (
              <button
                key={tab.value}
                className={cn(
                  'group flex cursor-pointer items-center justify-center overflow-hidden border-b-2 border-transparent',
                  isMobile ? 'px-2 py-2.5' : 'p-4',
                  isActive && 'border-[#dadada]'
                )}
                onClick={() => setActiveTab(tab.value)}
              >
                <div className="flex items-center gap-1">
                  <span
                    className={cn(
                      isActive ? 'text-[#dadada]' : 'text-[#7f7f7f] group-hover:text-[#acacac]'
                    )}
                  >
                    {tab.icon}
                  </span>
                  <span
                    className={cn(
                      'text-sm leading-5 font-medium whitespace-nowrap',
                      isActive ? 'text-[#dadada]' : 'text-[#7f7f7f] group-hover:text-[#acacac]'
                    )}
                  >
                    {t(tab.labelKey)}
                  </span>
                </div>
              </button>
            )
          })}
        </div>

        {/* 操作按钮：default / select / delete 三态切换 */}
        <div
          className={cn(
            'ml-auto flex shrink-0 items-center',
            isMobile ? 'gap-1.5' : 'gap-2'
          )}
        >
          {isMultiSelect && (
            <>
              {/* 下载按钮：选中数量 = 0 或正在下载时禁用；移动端仅图标以节省宽度 */}
              <button
                aria-label={t('download')}
                disabled={selectedAssets.size === 0 || isBatchDownloading}
                title={t('download')}
                className={cn(
                  actionButtonClass,
                  'border border-[rgba(255,255,255,0.08)]',
                  isMobile && 'px-2',
                  selectedAssets.size > 0 && !isBatchDownloading
                    ? 'cursor-pointer opacity-100 hover:bg-light/10'
                    : 'cursor-not-allowed opacity-50'
                )}
                onClick={handleBatchDownload}
              >
                <DownloadIcon />
                {!isMobile && (
                  <span className="text-sm leading-5 font-medium whitespace-nowrap text-[#dadada]">
                    {t('download')}
                  </span>
                )}
              </button>

              {/* 取消多选 */}
              <button
                className={cn(
                  actionButtonClass,
                  'cursor-pointer border border-[rgba(255,255,255,0.08)] hover:bg-light/10'
                )}
                onClick={exitActionMode}
              >
                <span className="text-sm leading-5 font-medium whitespace-nowrap text-[#dadada]">
                  {t('cancel')}
                </span>
              </button>

              {/* 使用 N 个资产/角色：选中数量 = 0 时禁用 */}
              <button
                disabled={selectedAssets.size === 0}
                className={cn(
                  actionButtonClass,
                  selectedAssets.size > 0
                    ? 'cursor-pointer bg-white text-[rgba(0,0,0,0.9)] backdrop-blur-[12px] hover:bg-white/90'
                    : 'cursor-not-allowed bg-[rgba(255,255,255,0.32)] text-[#dadada] backdrop-blur-[12px]'
                )}
                onClick={handleUseSelected}
              >
                <ArrowUpRightIcon
                  stroke={selectedAssets.size > 0 ? 'rgba(0,0,0,0.9)' : '#dadada'}
                />
                <span className="text-sm leading-5 font-medium whitespace-nowrap">
                  {t(isCharacterMode ? 'useNCharacters' : 'useNAssets', {
                    count: selectedAssets.size
                  })}
                </span>
              </button>
            </>
          )}

          {isDeleteMode && (
            <>
              {/* 删除按钮：默认禁用，选中后高亮成红色；移动端仅图标 */}
              <button
                aria-label={t('delete')}
                disabled={selectedAssets.size === 0 || isDeleting}
                title={t('delete')}
                className={cn(
                  actionButtonClass,
                  'border',
                  isMobile && 'px-2',
                  selectedAssets.size > 0 && !isDeleting
                    ? 'cursor-pointer border-[#e5484d]/40 bg-[#e5484d]/10 text-[#e5484d] hover:bg-[#e5484d]/20'
                    : 'cursor-not-allowed border-[rgba(255,255,255,0.08)] text-[#7f7f7f] opacity-60'
                )}
                onClick={handleRequestDeleteSelected}
              >
                <DeleteIcon
                  stroke={selectedAssets.size > 0 && !isDeleting ? '#e5484d' : '#7f7f7f'}
                />
                {!isMobile && (
                  <span className="text-sm leading-5 font-medium whitespace-nowrap">
                    {t('delete')}
                  </span>
                )}
              </button>

              {/* 取消删除 */}
              <button
                className={cn(
                  actionButtonClass,
                  'cursor-pointer border border-[rgba(255,255,255,0.08)] hover:bg-light/10'
                )}
                onClick={exitActionMode}
              >
                <span className="text-sm leading-5 font-medium whitespace-nowrap text-[#dadada]">
                  {t('cancel')}
                </span>
              </button>
            </>
          )}

          {actionMode === 'default' && (
            <>
              {/* 删除入口：仅角色库且列表非空时显示；移动端仅图标 */}
              {isCharacterMode && !isEmpty && (
                <button
                  aria-label={t('delete')}
                  title={t('delete')}
                  className={cn(
                    actionButtonClass,
                    'cursor-pointer border border-[rgba(255,255,255,0.08)] hover:bg-light/10',
                    isMobile && 'px-2'
                  )}
                  onClick={enterDeleteMode}
                >
                  <DeleteIcon />
                  {!isMobile && (
                    <span className="text-sm leading-5 font-medium whitespace-nowrap text-[#dadada]">
                      {t('delete')}
                    </span>
                  )}
                </button>
              )}

              {/* 选择多个资产按钮（常驻显示；超出 maxImages 时由点击逻辑 toast 阻断） */}
              <button
                className={cn(
                  actionButtonClass,
                  'cursor-pointer border border-[rgba(255,255,255,0.08)] hover:bg-light/10'
                )}
                onClick={enterSelectMode}
              >
                <SelectIcon />
                <span className="text-sm leading-5 font-medium whitespace-nowrap text-[#dadada]">
                  {t(isCharacterMode ? 'selectMultipleCharacters' : 'selectMultiple')}
                </span>
              </button>

              {/* 上传资产按钮 */}
              <UploadAssetDialog isSaving={isSavingUpload} mode={mode} onSave={handleUploadSave}>
                <button
                  className={cn(
                    actionButtonClass,
                    'cursor-pointer bg-white backdrop-blur-[12px]'
                  )}
                >
                  <UploadIcon />
                  <span className="text-sm leading-5 font-medium whitespace-nowrap text-[rgba(0,0,0,0.9)]">
                    {t(isCharacterMode ? 'uploadCharacter' : 'uploadAsset')}
                  </span>
                </button>
              </UploadAssetDialog>
            </>
          )}
        </div>
      </div>

      {/* 分隔线 */}
      <div className={cn('h-px shrink-0 bg-[rgba(255,255,255,0.08)]', isMobile ? 'mx-4' : 'mx-6')} />

      {/* Content */}
      <div
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.3) transparent' }}
        className={cn(
          'flex-1 overflow-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/30 [&::-webkit-scrollbar-thumb:hover]:bg-white/50 [&::-webkit-scrollbar-track]:bg-transparent',
          isMobile ? 'p-4' : compact ? 'p-4' : 'p-6'
        )}
      >
        {isLoading ? null : isEmpty ? (
          <AssetLibraryEmpty isCharacterMode={isCharacterMode} />
        ) : (
          <AssetLibraryGrid
            assets={filteredAssets}
            compact={compact}
            isMobile={isMobile}
            isMultiSelect={isPickingActive}
            selectedAssets={selectedAssets}
            onAssetClick={handleAssetClick}
            onDeleteAsset={handleRequestDeleteOne}
            onEditAsset={asset => setEditingAsset(asset)}
            onPreviewAsset={asset => {
              setViewerSource(assetToDisplaySource(asset))
              setViewerOpen(true)
            }}
          />
        )}
      </div>

      {/* Edit dialog (uses upload-asset modal UI) */}
      <Dialog open={Boolean(editingAsset)} onOpenChange={next => !next && closeEditing()}>
        <DialogContent
          className="z-100 flex h-[500px] w-[958px] max-w-[90vw] flex-col gap-0 overflow-hidden border-0 bg-transparent p-0"
          overlayClassName="bg-black/50"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">Edit Asset</DialogTitle>
          <DialogDescription className="sr-only">Edit Asset</DialogDescription>
          <UploadAssetContent
            key={editingAsset?.id}
            file={editingFile}
            initialValues={editingInitialValues}
            isSaving={isSavingEdit}
            mode={mode}
            previewUrl={pickAssetOriginalUrl(editingAsset)}
            onClose={closeEditing}
            onSave={handleEditSave}
            onFileChange={file => {
              setEditingFile(file)
              const url = URL.createObjectURL(file)
              setEditingAsset(prev => (prev ? { ...prev, url } : prev))
            }}
          />
        </DialogContent>
      </Dialog>

      <ImageViewer
        alt=""
        open={viewerOpen}
        source={viewerSource}
        src={viewerSource ? resolveRemoteDisplayUrl(viewerSource) : ''}
        onOpenChange={open => {
          setViewerOpen(open)
          if (!open) setViewerSource(null)
        }}
      />

      {/* 删除二次确认弹窗（多选/单选共用） */}
      <AlertDialog
        open={deleteConfirmOpen}
        onOpenChange={next => {
          if (isDeleting) return
          setDeleteConfirmOpen(next)
          if (!next) setPendingDeleteIds([])
        }}
      >
        <AlertDialogContent
          className="z-100 w-[480px] max-w-[90vw] rounded-2xl border border-[rgba(255,255,255,0.08)] bg-light-800 p-6 text-[#dadada] sm:max-w-[480px]"
          onClick={e => e.stopPropagation()}
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-semibold text-[#dadada]">
              {t('deleteImagesTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#7f7f7f]">
              {t('deleteImagesDescription', { count: pendingDeleteIds.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-2">
            <AlertDialogCancel
              className="rounded-md border-[rgba(255,255,255,0.08)] bg-transparent text-[#dadada] hover:bg-light/10 hover:text-[#dadada]"
              disabled={isDeleting}
            >
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-md bg-[#e5484d] text-white hover:bg-[#e5484d]/90"
              disabled={isDeleting}
              onClick={handleConfirmDelete}
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

const AssetLibraryEmpty = ({ isCharacterMode }: { isCharacterMode?: boolean }) => {
  const t = useTranslations('common')

  return (
    <div className="flex size-full flex-col items-center justify-center gap-4 py-16">
      <div className="flex size-16 items-center justify-center rounded-full bg-light/10">
        <FolderIcon className="size-8 text-[#7f7f7f]" />
      </div>
      <div className="flex flex-col items-center gap-2 text-center">
        <h3 className="text-lg font-medium text-[#dadada]">
          {t(isCharacterMode ? 'noCharacters' : 'noAssets')}
        </h3>
        <p className="max-w-xs text-sm text-[#7f7f7f]">
          {t(isCharacterMode ? 'noCharactersDescription' : 'noAssetsDescription')}
        </p>
      </div>
    </div>
  )
}

interface AssetLibraryGridProps {
  assets: Asset[]
  selectedAssets: Set<string>
  isMultiSelect: boolean
  compact?: boolean
  isMobile?: boolean
  onAssetClick: (asset: Asset) => void
  onDeleteAsset: (assetId: string) => void
  onEditAsset: (asset: Asset) => void
  onPreviewAsset: (asset: Asset) => void
}

function assetToDisplaySource(asset: Asset): ImageDisplaySource {
  const localPreview =
    getCharacterLocalBlobUrl(asset.resourceId ?? asset.id) ||
    (asset.url?.startsWith('blob:') ? asset.url : undefined)
  return {
    res: {
      imageUrl: asset.imageUrl || asset.thumbnailUrl || asset.url,
      imageUrlV1: asset.imageUrlV1,
      thumbnailUrl: asset.thumbnailUrl,
      thumbnailUrlV1: asset.thumbnailUrlV1,
      showImageUrl: asset.showImageUrl || asset.url
    },
    previewUrl: localPreview || asset.showImageUrl || asset.url,
    localPreviewUrl: localPreview
  }
}

const AssetGridImage = ({ asset }: { asset: Asset }) => {
  const source = assetToDisplaySource(asset)
  return <ResolvedImage fill alt="" className="object-cover" source={source} />
}

const AssetLibraryGrid = ({
  assets,
  selectedAssets,
  isMultiSelect,
  compact = false,
  isMobile = false,
  onAssetClick,
  onDeleteAsset,
  onEditAsset,
  onPreviewAsset
}: AssetLibraryGridProps) => {
  return (
    <div
      className={cn(
        'grid gap-2',
        isMobile ? 'grid-cols-3' : compact ? 'grid-cols-5' : 'grid-cols-6'
      )}
    >
      {assets.map(asset => {
        const isSelected = selectedAssets.has(asset.id)
        return (
          <div
            key={asset.id}
            className="group relative aspect-square cursor-pointer overflow-hidden rounded-sm"
            onClick={() => onAssetClick(asset)}
          >
            <AssetGridImage asset={asset} />
            {/* Hover controls (Figma 110:13922) */}
            <div className="pointer-events-none absolute top-2 right-2 flex items-center gap-0.5 rounded-[8px] bg-[rgba(0,0,0,0.9)] p-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              <button
                className="pointer-events-auto relative size-7 rounded-[6px] hover:bg-white/10"
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onEditAsset(asset)
                }}
              >
                <EditIcon />
              </button>
              <div className="h-3 w-px bg-[rgba(255,255,255,0.16)]" />
              <button
                className="pointer-events-auto relative size-7 rounded-[6px] hover:bg-white/10"
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onDeleteAsset(asset.id)
                }}
              >
                <TrashIcon />
              </button>
            </div>

            <div className="pointer-events-none absolute right-2 bottom-2 flex items-start rounded-[8px] bg-[rgba(0,0,0,0.9)] p-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              <button
                className="pointer-events-auto relative size-7 rounded-[6px] hover:bg-white/10"
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onPreviewAsset(asset)
                }}
              >
                <ZoomInIcon />
              </button>
            </div>
            {/* 选中遮罩 */}
            {isSelected && <div className="absolute inset-0 bg-[rgba(0,0,0,0.32)]" />}
            {/* 复选框 */}
            {isMultiSelect && (
              <div
                className={cn(
                  'absolute top-2 left-2 size-5 overflow-hidden rounded bg-[rgba(0,0,0,0.8)] backdrop-blur-[12px]',
                  isSelected && 'bg-white'
                )}
              >
                {isSelected && (
                  <svg className="size-full text-black" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      clipRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      fillRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

const EditIcon = () => (
  <svg
    className="absolute top-1/2 left-1/2 size-4 -translate-x-1/2 -translate-y-1/2"
    fill="none"
    viewBox="0 0 16 16"
  >
    <path
      d="M2.5 11.5V13.5H4.5L12.3 5.7L10.3 3.7L2.5 11.5Z"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M9.6 4.4L11.6 6.4"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const TrashIcon = () => (
  <svg
    className="absolute top-1/2 left-1/2 size-4 -translate-x-1/2 -translate-y-1/2"
    fill="none"
    viewBox="0 0 16 16"
  >
    <path
      d="M3.75 4.75V13.25C3.75 13.8023 4.19772 14.25 4.75 14.25H11.25C11.8023 14.25 12.25 13.8023 12.25 13.25V4.75"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M6.5 7.5V11.5"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M9.5 7.5V11.5"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M2.5 4.75H13.5"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M5.5 4.75V2.75C5.5 2.19772 5.94772 1.75 6.5 1.75H9.5C10.0523 1.75 10.5 2.19772 10.5 2.75V4.75"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const ZoomInIcon = () => (
  <svg
    className="absolute top-1/2 left-1/2 size-4 -translate-x-1/2 -translate-y-1/2"
    fill="none"
    viewBox="0 0 16 16"
  >
    <path
      d="M7 12.5C9.48528 12.5 11.5 10.4853 11.5 8C11.5 5.51472 9.48528 3.5 7 3.5C4.51472 3.5 2.5 5.51472 2.5 8C2.5 10.4853 4.51472 12.5 7 12.5Z"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M10.5 11.5L13.5 14.5"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M7 6V10"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M5 8H9"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

// Icons
const RefreshIcon = ({ className }: { className?: string }) => (
  <svg className={cn('size-5', className)} fill="none" viewBox="0 0 20 20">
    <path
      d="M3.75 3.125V6.875H4.0675L4.06958 6.87292L7.5 3.4425"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M5 6.5625C5.70313 5.16875 7.00625 4.10625 8.57292 3.70833C11.2592 3.02708 14.0229 4.52708 14.9271 7.14583"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M16.25 16.875V13.125H15.9325L15.9304 13.1271L12.5 16.5575"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M15 13.4379C14.2969 14.8317 12.9938 15.8942 11.4271 16.2921C8.74083 16.9733 5.97708 15.4733 5.07292 12.8546"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

const AllIcon = () => (
  <svg className="size-5" fill="none" viewBox="0 0 20 20">
    <path
      d="M13.0625 17.5H15.625C15.7908 17.5 15.9497 17.4342 16.0669 17.3169C16.1842 17.1997 16.25 17.0408 16.25 16.875V6.875L11.875 2.5H4.375C4.20924 2.5 4.05027 2.56585 3.93306 2.68306C3.81585 2.80027 3.75 2.95924 3.75 3.125V8.4375"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M11.5625 2.5V7.1875H16.25"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M8 16C6.34315 16 5 14.6569 5 13C5 11.3431 6.34315 10 8 10C9.65685 10 11 11.3431 11 13C11 14.6569 9.65685 16 8 16Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M5.5 15.4868L4 16.9868"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const CharactersIcon = () => (
  <svg className="size-5" fill="none" viewBox="0 0 20 20">
    <path
      d="M4.24244 15.8945C4.0461 16.2903 4.20779 16.7703 4.60359 16.9667C4.9994 17.163 5.47943 17.0013 5.67577 16.6055L4.95911 16.25L4.24244 15.8945ZM14.3206 16.6055C14.5169 17.0013 14.9969 17.163 15.3927 16.9667C15.7885 16.7703 15.9502 16.2903 15.7539 15.8945L15.0372 16.25L14.3206 16.6055ZM13.125 10H12.325C12.325 11.2841 11.2841 12.325 10 12.325V13.125V13.925C12.1677 13.925 13.925 12.1677 13.925 10H13.125ZM10 13.125V12.325C8.71594 12.325 7.675 11.2841 7.675 10H6.875H6.075C6.075 12.1677 7.83228 13.925 10 13.925V13.125ZM6.875 10H7.675C7.675 8.71594 8.71594 7.675 10 7.675V6.875V6.075C7.83228 6.075 6.075 7.83228 6.075 10H6.875ZM10 6.875V7.675C11.2841 7.675 12.325 8.71594 12.325 10H13.125H13.925C13.925 7.83228 12.1677 6.075 10 6.075V6.875ZM4.95911 16.25L5.67577 16.6055C6.07512 15.8005 6.69136 15.123 7.45505 14.6493L7.03339 13.9695L6.61173 13.2896C5.5948 13.9203 4.77422 14.8225 4.24244 15.8945L4.95911 16.25ZM7.03339 13.9695L7.45505 14.6493C8.21873 14.1757 9.09952 13.9247 9.99817 13.9247V13.1247V12.3247C8.80153 12.3247 7.62866 12.6589 6.61173 13.2896L7.03339 13.9695ZM9.99817 13.1247V13.9247C10.8968 13.9247 11.7776 14.1757 12.5413 14.6493L12.9629 13.9695L13.3846 13.2896C12.3677 12.6589 11.1948 12.3247 9.99817 12.3247V13.1247ZM12.9629 13.9695L12.5413 14.6493C13.305 15.123 13.9212 15.8005 14.3206 16.6055L15.0372 16.25L15.7539 15.8945C15.2221 14.8225 14.4015 13.9203 13.3846 13.2896L12.9629 13.9695ZM8 4V4.8H12V4V3.2H8V4ZM18 10H17.2V11H18H18.8V10H18ZM12 17V16.2H8V17V17.8H12V17ZM2 11H2.8V10H2H1.2V11H2ZM8 17V16.2C6.56317 16.2 5.56603 16.1983 4.81478 16.0973C4.08655 15.9994 3.71029 15.8216 3.44437 15.5556L2.87868 16.1213L2.31299 16.687C2.92575 17.2998 3.69593 17.5613 4.60159 17.683C5.48423 17.8017 6.6084 17.8 8 17.8V17ZM2 11H1.2C1.2 12.3916 1.1983 13.5158 1.31697 14.3984C1.43873 15.3041 1.70024 16.0743 2.31299 16.687L2.87868 16.1213L3.44437 15.5556C3.17844 15.2897 3.00061 14.9134 2.9027 14.1852C2.8017 13.434 2.8 12.4368 2.8 11H2ZM18 11H17.2C17.2 12.4368 17.1983 13.434 17.0973 14.1852C16.9994 14.9134 16.8216 15.2897 16.5556 15.5556L17.1213 16.1213L17.687 16.687C18.2998 16.0743 18.5613 15.3041 18.683 14.3984C18.8017 13.5158 18.8 12.3916 18.8 11H18ZM12 17V17.8C13.3916 17.8 14.5158 17.8017 15.3984 17.683C16.3041 17.5613 17.0743 17.2998 17.687 16.687L17.1213 16.1213L16.5556 15.5556C16.2897 15.8216 15.9134 15.9994 15.1852 16.0973C14.434 16.1983 13.4368 16.2 12 16.2V17ZM12 4V4.8C13.4368 4.8 14.434 4.8017 15.1852 4.9027C15.9134 5.00061 16.2897 5.17844 16.5556 5.44437L17.1213 4.87868L17.687 4.31299C17.0743 3.70024 16.3041 3.43873 15.3984 3.31697C14.5158 3.1983 13.3916 3.2 12 3.2V4ZM18 10H18.8C18.8 8.6084 18.8017 7.48423 18.683 6.60159C18.5613 5.69593 18.2998 4.92575 17.687 4.31299L17.1213 4.87868L16.5556 5.44437C16.8216 5.71029 16.9994 6.08655 17.0973 6.81478C17.1983 7.56603 17.2 8.56317 17.2 10H18ZM8 4V3.2C6.6084 3.2 5.48423 3.1983 4.60159 3.31697C3.69593 3.43873 2.92575 3.70024 2.31299 4.31299L2.87868 4.87868L3.44437 5.44437C3.71029 5.17844 4.08655 5.00061 4.81478 4.9027C5.56603 4.8017 6.56317 4.8 8 4.8V4ZM2 10H2.8C2.8 8.56317 2.8017 7.56603 2.9027 6.81478C3.00061 6.08655 3.17844 5.71029 3.44437 5.44437L2.87868 4.87868L2.31299 4.31299C1.70024 4.92575 1.43873 5.69593 1.31697 6.60159C1.1983 7.48423 1.2 8.6084 1.2 10H2Z"
      fill="currentColor"
    />
  </svg>
)

const PropsIcon = () => (
  <svg className="size-5" fill="none" viewBox="0 0 20 20">
    <path
      d="M9.98672 2.5C9.98321 4.48452 9.1933 6.38676 7.79003 7.79003C6.38676 9.1933 4.48452 9.98321 2.5 9.98672M17.5001 10.0134C15.5156 10.0169 13.6134 10.8068 12.2101 12.2101C10.8068 13.6134 10.0169 15.5156 10.0134 17.5001M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const SceneIcon = () => (
  <svg className="size-5" fill="none" viewBox="0 0 20 20">
    <path
      d="M2.5 10H9.0625M1.875 15.625H18.125M3.4375 10V15.625M8.125 10V15.625M2.5 12.8125H9.0625M15 15.625V12.5M15 2.5L17.5 12.5H12.5L15 2.5ZM10.625 5.3125C10.625 6.17544 9.92544 6.875 9.0625 6.875C8.19956 6.875 7.5 6.17544 7.5 5.3125C7.5 4.44956 8.19956 3.75 9.0625 3.75C9.92544 3.75 10.625 4.44956 10.625 5.3125Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const ImageGalleryIcon = () => (
  <svg className="size-3.5" fill="none" viewBox="0 0 16 16">
    <path
      d="M14.8008 11.8L10.4651 7.46434C10.0917 7.09094 9.49231 7.07223 9.09634 7.42161L1.30078 14.3M13.8008 0.800049H1.80078C1.2485 0.800049 0.800781 1.24776 0.800781 1.80005V13.8C0.800781 14.3523 1.2485 14.8 1.80078 14.8H13.8008C14.3531 14.8 14.8008 14.3523 14.8008 13.8V1.80005C14.8008 1.24776 14.3531 0.800049 13.8008 0.800049Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeWidth="1.6"
    />
  </svg>
)

const DeleteIcon = ({ stroke = '#dadada' }: { stroke?: string }) => (
  <svg className="size-4" fill="none" viewBox="0 0 16 16">
    <path
      d="M3.75 4.75V13.25C3.75 13.8023 4.19772 14.25 4.75 14.25H11.25C11.8023 14.25 12.25 13.8023 12.25 13.25V4.75"
      stroke={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M6.5 7.5V11.5"
      stroke={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M9.5 7.5V11.5"
      stroke={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M2.5 4.75H13.5"
      stroke={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M5.5 4.75V2.75C5.5 2.19772 5.94772 1.75 6.5 1.75H9.5C10.0523 1.75 10.5 2.19772 10.5 2.75V4.75"
      stroke={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

const SelectIcon = () => (
  <svg className="size-4 text-[#dadada]" fill="none" viewBox="0 0 16 16">
    <path
      d="M2.5 9.78675V5.2913C2.5 5.18762 2.54119 5.0882 2.61449 5.01489C2.6878 4.94158 2.78723 4.90039 2.89091 4.90039H10.7091C10.8128 4.90039 10.9122 4.94158 10.9855 5.01489C11.0588 5.0882 11.1 5.18762 11.1 5.2913V13.1095C11.1 13.2132 11.0588 13.3126 10.9855 13.3859C10.9122 13.4592 10.8128 13.5004 10.7091 13.5004H7.38636"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M3.69092 2.5H13.1091C13.2128 2.5 13.3122 2.54119 13.3855 2.61449C13.4588 2.6878 13.5 2.78723 13.5 2.89091V12.3091"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
    <path
      d="M2.5 12L4 13.5L7.5 10"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const UploadIcon = () => (
  <svg className="size-4" fill="none" viewBox="0 0 16 16">
    <path
      d="M2.7 13.5H13.3"
      stroke="rgba(0,0,0,0.9)"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M8 10.4V2.4"
      stroke="rgba(0,0,0,0.9)"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M4 6.4L8 2.4L12 6.4"
      stroke="rgba(0,0,0,0.9)"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

const DownloadIcon = () => (
  <svg className="size-4" fill="none" viewBox="0 0 16 16">
    <path
      d="M2.7 13.5H13.3"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M8 2.4V10.4"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M4 6.4L8 10.4L12 6.4"
      stroke="#dadada"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

const ArrowUpRightIcon = ({ stroke = 'rgba(0,0,0,0.9)' }: { stroke?: string }) => (
  <svg className="size-4" fill="none" viewBox="0 0 16 16">
    <path
      d="M4 12L12 4"
      stroke={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M5.5 4H12V10.5"
      stroke={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

const FolderIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 32 32">
    <path
      d="M4 8C4 6.89543 4.89543 6 6 6H12L14 9H26C27.1046 9 28 9.89543 28 11V24C28 25.1046 27.1046 26 26 26H6C4.89543 26 4 25.1046 4 24V8Z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
)

export default AssetLibraryDialog